import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import PageWrapper from '@/components/feature/PageWrapper';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { movies, categories } from '@/mocks/data';

const categoryHeroImages: Record<string, string> = {
  'crime-thriller': 'https://readdy.ai/api/search-image?query=Korean%20crime%20thriller%20film%20noir%20style%2C%20dark%20interrogation%20room%20with%20dramatic%20lighting%2C%20rainy%20night%20street%20scene%2C%20suspenseful%20atmosphere%2C%20cinematic%20photography%20with%20shadows%2C%20no%20text%20no%20letters&width=1800&height=600&seq=cat-crime-thriller&orientation=landscape',
  'romance-drama': 'https://readdy.ai/api/search-image?query=Korean%20romance%20drama%20film%2C%20soft%20warm%20lighting%20with%20cherry%20blossoms%2C%20emotional%20couple%20silhouette%2C%20nostalgic%20pastel%20tones%2C%20Korean%20cinema%20romantic%20aesthetic%2C%20no%20text%20no%20letters&width=1800&height=600&seq=cat-romance-drama&orientation=landscape',
  'horror-thriller': 'https://readdy.ai/api/search-image?query=Korean%20horror%20film%20dark%20supernatural%20village%20scene%2C%20foggy%20forest%20with%20ominous%20red%20lanterns%2C%20ritualistic%20atmosphere%2C%20creepy%20folk%20horror%20aesthetic%2C%20cinematic%20photography%2C%20no%20text%20no%20letters&width=1800&height=600&seq=cat-horror-thriller&orientation=landscape',
  'action-adventure': 'https://readdy.ai/api/search-image?query=Korean%20action%20film%20intense%20chase%20scene%2C%20urban%20night%20setting%20with%20neon%20lights%2C%20dynamic%20movement%20blur%2C%20explosive%20action%20cinematography%2C%20Korean%20thriller%20aesthetic%2C%20no%20text%20no%20letters&width=1800&height=600&seq=cat-action-adventure&orientation=landscape',
  'social-realism': 'https://readdy.ai/api/search-image?query=Korean%20social%20realism%20film%2C%20urban%20alley%20neon%20signs%20contrast%2C%20gritty%20realistic%20street%20photography%2C%20working%20class%20neighborhood%2C%20Korean%20cinema%20drama%20aesthetic%2C%20no%20text%20no%20letters&width=1800&height=600&seq=cat-social-realism&orientation=landscape',
  'historical': 'https://readdy.ai/api/search-image?query=Korean%20historical%20film%20period%20drama%2C%20ancient%20Korean%20palace%20courtyard%20with%20traditional%20architecture%2C%20misty%20morning%20atmosphere%2C%20historical%20costume%20drama%20aesthetic%2C%20no%20text%20no%20letters&width=1800&height=600&seq=cat-historical&orientation=landscape',
};

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'crime-thriller');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const category = categories.find((c) => c.slug === activeSlug);
  const categoryMovies = movies.filter((m) => m.categorySlug === activeSlug);
  const heroImage = categoryHeroImages[activeSlug] || categoryHeroImages['crime-thriller'];

  if (!category) return null;

  return (
    <>
      <SEOHead
        title={`${category.name} - 韩国${category.name}电影推荐 | 韩影深解`}
        description={category.description}
        keywords={`韩国${category.name}电影,${category.name}电影推荐,韩国电影,韩国电影排行榜`}
        canonicalUrl={`https://hanyingshenjie.com/category/${activeSlug}`}
      />
      <main>
        <PageWrapper heroImage={heroImage} title={category.name} subtitle={category.description} />

        <div className="w-full bg-background-100 border-b border-background-300/20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '电影分类', href: '/category/crime-thriller' },
                { label: category.name },
              ]}
            />
          </div>
        </div>

        <nav className="w-full bg-background-50 border-b border-background-300/20 sticky top-16 z-40">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs md:text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-500 hover:text-primary-500 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </nav>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {categoryMovies.map((movie) => (
                <MovieCard key={movie.id} id={movie.id} />
              ))}
            </div>
            {categoryMovies.length === 0 && (
              <div className="text-center py-20">
                <p className="text-foreground-500">该分类暂无电影</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}